import {
  DictionaryLanguage,
  PassingProbationEnum,
  ReferralPaymentPayRollStatus,
  ReferralPaymentStatus,
  RolesValues,
  StatusLifecycleStatuses,
  TranslationUnion,
} from '@constants';

import {
  CandidateStatusListItem,
  FullEmployeeRequest,
  GetEmployeeAuthDataResponse,
  LocalizedShortCandidateItemResponse,
  LocalizedShortEmployeeItemResponse,
  SelectOption,
  ShortEmployeeItemResponse,
  UpdateEmployeeAuthDataRequest,
} from '@innowise-group/core';

export interface EntityOptions {
  ids: string[];
  options: { [key: string]: SelectOption };
}

export interface AdministrationLocationSelectOptions extends SelectOption {
  hasCompanyOffice: boolean;
}

export interface AdministrationState {
  employees: {
    isLoading: boolean;
    employeesIds: number[];
    employees: { [key: number]: ShortEmployeeItem };
    totalElements: number;
    totalPages: number;

    employeesEmailsList: FullEmployeeRequest[];
    employeesEmailsListLoading: boolean;

    pagable: {
      pageSizes: { title: string; value: string }[];
      pageSize: number;
      currentPage: number;
    };
    vacanciesOptions: EntityOptions;
    requestsOptions: EntityOptions;

    searchedVacanciesOptions: SelectOption[];
    searchedRequestsOptions: SelectOption[];
    optionsLoading: boolean;

    userToAdd: FullEmployeeRequest;
    userLoading: boolean;
  };
  dictionaries: {
    isLoading: boolean;
    data: {
      content: Array<TranslationUnion>;
      first?: boolean;
      last?: boolean;
      number?: number;
      size?: number;
      totalElements?: number;
      totalPages?: number;
    };
    totalElements: number;
    totalPages: number;
    domainTypename: string;
    candidateSourceId: string;
    searchValue: string;
    sortLanguage: DictionaryLanguage;
    sortDirection: string;

    pagable: {
      pageSizes: { title: string; value: string }[];
      pageSize: number;
      currentPage: number;
    };
  };
  locations: {
    citiesDataByLanguage: { [key: string]: AdministrationLocationSelectOptions[] };
    isLoading: boolean;
  };
  statuses: {
    isLoading: boolean;
    currentStatus: AdministrationStatusItemResponse;
  };
  requestApprove: AdministrationRequestApproveState;
  referral: ReferralState;
  units: AdministrationUnitsState;
  sourcing: AdministrationSourcingState;
}

export interface AdministrationSourcingState {
  isLoading: boolean;
  ids: string[];
  items: { [key: string]: SourcingItemResponse };
  totalElements: number;
  totalPages: number;
}

export interface AdministrationRequestApproveState {
  isLoading: boolean;
  items: Partial<Record<AdministrationRequestApproveReasons, Array<ShortEmployeeItemResponse>>>;
}

export interface SourcingItemResponse extends LocalizedShortEmployeeItemResponse {
  requiredNumberOfEvents: number;
}

export interface SourcingItem {
  employeeId: string;
  requiredNumberOfEvents: string;
}

export interface ReferralState {
  isLoading: boolean;
  isManagerLoading: boolean;
  isExportLoading: boolean;
  managerOptions: SelectOption[];
  isManagerOptionsLoading: boolean;
  manager: LocalizedShortEmployeeItemResponse | null;
  items: ReferralItemResponse[];
}

export interface ReferralItemResponse {
  yearMonth: string;
  reward: number;
  candidateReferralDtos?: ReferralsCandidateItem[];
  isLoading?: boolean;
  isLoaded?: boolean;
}

export interface ReferralsCandidateItem {
  id: number;
  accepted?: boolean;
  editable?: boolean;
  needsAction: boolean;
  passingProbation?: PassingProbationEnum;
  referralComment: string;
  startGrade: string;
  reward: number;
  orgUnitDtos: {
    headId: number;
    id: number;
    orgUnitName: string;
    orgUnitType: {
      id: number;
      orgUnitTypeMaxSize: number;
      orgUnitTypeNameEn: string;
      orgUnitTypeNameRu: string;
    };
    reportsToId: number;
  }[];
  beginEvent: {
    createdDate: string;
    id: number;
    periodFrom?: string;
    periodTo?: string;
    status: {
      group: string;
      id: string;
      lifecycleStatus: string;
      localizedName: string;
    };
    subStatus: {
      group: string;
      id: string;
      lifecycleStatus: string;
      localizedName: string;
    };
  };
  endEvent?: {
    createdDate: string;
    id: number;
    periodFrom?: string;
    periodTo?: string;
    status: {
      group: string;
      id: string;
      lifecycleStatus: string;
      localizedName: string;
    };
    subStatus: {
      group: string;
      id: string;
      lifecycleStatus: string;
      localizedName: string;
    };
  };
  jobTitle: {
    parentId?: string;
    translation: string;
    valueId: string;
  };
  recommender: {
    email: string;
    firstNameEn: string;
    firstNameRu: string;
    id: number;
    lastNameEn: string;
    lastNameRu: string;
    miniPhotoHrmUrl?: string;
    photoHrmUrl?: string;
    localizedFullName: string;
    lifecycleStatus: string;
  };
  candidate: LocalizedShortCandidateItemResponse;
  status: ReferralPaymentStatus;
  payrollStatus?: ReferralPaymentPayRollStatus;
  manualChangeMessage?: string;
  payrollReportPeriod?: number;
  payrollReportPeriodDate?: string;
  isAutoExtendedProbation: boolean;
}

export interface AdministrationUnitsState {
  isLoading: boolean;
  ids: number[];
  items: { [key: number]: UnitItem };
}

export interface UnitItem {
  id: number;
  nameRu: string;
  nameEn: string;
  isActual: boolean;
}

export interface LocalizedShortEmployeeItem extends ShortEmployeeItem {
  localizedFullName: string;
  localizedFirstName: string;
  localizedLastName: string;
}

export interface ShortEmployeeItem extends Omit<ShortEmployeeItemResponse, 'authData' | 'employeeId'> {
  id: number;
  newRole: RolesValues;
  authData: {
    profileAccess: boolean;
    databaseAccess: boolean;
    financeAccess: boolean;
    permittedProfessions: string[];
    permittedVacancies: string[];
    permittedVacancyRequestIds: string[];
  } & GetEmployeeAuthDataResponse;
}

export interface ShoShortEmployeeItemState extends Omit<ShortEmployeeItem, 'authData'> {
  authData: {
    profileAccess: boolean;
    databaseAccess: boolean;
    financeAccess: boolean;
  } & UpdateEmployeeAuthDataRequest;
}

export interface MergeLocationsItem {
  cities: AdministrationLocationSelectOptions[];
  correctCityValue: string;
  countryId: string;
  cityNames: {
    [key: string]: string;
  };
}

export interface AdministrationStatusItem {
  group: string;
  statusName: { [key: string]: string };
  subStatusNames: { id: string; names: { [key: string]: string }; lifecycleStatus: StatusLifecycleStatuses }[];
  deletedSubStatusNames: { id: string; names: { [key: string]: string }; lifecycleStatus: StatusLifecycleStatuses }[];
  VACANCY: AdministrationStatusItemField;
  SUB_STATUS: AdministrationStatusItemField;
  REMINDER: AdministrationStatusItemField;
  CALENDAR: AdministrationStatusItemField;
  FILE: AdministrationStatusItemField;
  LINK: AdministrationStatusItemField;
  NOTIFICATION: AdministrationStatusItemField;
  AGREEMENT: AdministrationStatusItemField;
  FEEDBACK: AdministrationStatusItemField;
}

export interface AdministrationStatusItemField {
  isExist: boolean;
  isMandatory: boolean;
}

export interface AdministrationStatusItemResponse extends Omit<CandidateStatusListItem, 'localizedName' | 'subStatus'> {
  localizedNames: { [key: string]: string };
  subStatus: { id: string; localizedNames: { [key: string]: string }; lifecycleStatus: StatusLifecycleStatuses }[];
}

export enum AdministrationRequestApproveReasons {
  NOT_BILLABLE = 'NOT_BILLABLE',
}

export interface SyncEventFormState {
  id: string;
}
